import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { Box, Card, Grid, styled, useTheme } from '@mui/material';
import HeaderToolbar from 'page-sections/calendar/HeaderToolbar';
import { type FC, useEffect, useRef, useState } from 'react';
import AddEventModal, { AddWorkFormData } from './AddEventModal';
import { EventClickArg, EventContentArg, EventInput } from '@fullcalendar/core';
import ViewEventModal, { UpdateEvent } from './ViewEventModal';
import { calendarEntriesApi, seasonApi } from '../../../api';
import { CalendarEntryEntity, CalendarEntryEntityTypeEnum } from '../../../api/generated';
import { useTranslation } from 'react-i18next';
import { addDays, format, startOfDay } from 'date-fns';
import toast, { Toaster } from 'react-hot-toast';
import { useSeason } from 'contexts/SeasonContext';
import { GlobalCursorState } from 'pages/employees/calendar';
import BrushIcon from '@mui/icons-material/Brush';
import itLocale from '@fullcalendar/core/locales/it'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'
import frLocale from '@fullcalendar/core/locales/fr'
import { firstLetterUpperCase } from 'utils/firstLetterUpperCase';
import { fadeColor } from 'utils/utils';
import { isHoliday } from 'utils/holidays';

// styled component
export const CalendarWrapper = styled(Box)(({ theme }) => ({
  padding: '1.5rem',
  '& .fc-theme-standard td, & .fc-theme-standard th': {
    border: 'none',
  },
  '& .fc-theme-standard, & .fc-scrollgrid': {
    border: 'none !important',
  },
  '& .fc .fc-scroller-harness-liquid': {
    marginTop: '1rem',
  },
  '& .fc th': {
    color: theme.palette.primary.main,
  },
  '& .fc-daygrid-day-top': {
    justifyContent: 'center',
    alignItems: 'center',
    height: 'inherit',
  },
  '& .fc .fc-daygrid-day.fc-day-today': {
    borderRadius: '8px',
    border: `5px solid ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
  },
  '& .fc-daygrid-day-number': {
    fontWeight: 600,
  },
  '& .fc-h-event': {
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    textAlign: 'center',
  },
  '& .fc-daygrid-day-bottom a': {
    backgroundColor: theme.palette.primary.main,
    display: 'block',
    textAlign: 'center',
    color: theme.palette.background.paper,
    fontSize: 10,
    fontWeight: 500,
  },
  '& .fc .fc-popover': {
    border: 'none',
    borderRadius: '8px',
    boxShadow: theme.shadows[1],
    zIndex: 111,
  },
  '& .fc .fc-popover-header': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: 12,
    fontWeight: 600,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  '& .fc .fc-daygrid-event-harness': {
    padding: 3,
    cursor: 'pointer',
  },

  '& .holiday-number': {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  '& .regional-holiday-number': {
    color: theme.palette.warning.main,
    fontWeight: 700,
  },
}));

interface Props {
  employeeId: number;
  cursorType?: GlobalCursorState;
  // eventCount should be setted every time data being fetched or the displayed month changes
  setEventCount: (eventCount: Record<string, number>) => void;
  fetchDaysOffData?: () => void;
  userMode: boolean;
  height?: number;
}

const Calendar: FC<Props> = ({ employeeId, cursorType, setEventCount, userMode, fetchDaysOffData, height }) => {
  const isMobile = screen.width <= 768;
  const isTablet = screen.width <= 1024;
  const { seasonId } = useSeason();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const calendarRef = useRef<FullCalendar>(null);
  const [date, setDate] = useState(new Date());
  const [addEventState, setAddEventState] = useState<{
    isOpened: boolean;
    data?: Partial<AddWorkFormData>;
  }>({
    isOpened: false,
  });
  const [viewEventState, setViewEventState] = useState<{
    isOpened: boolean;
    id?: number;
    color?: string;
  }>({
    isOpened: false,
  });
  const [initialEvents, setInitialEvents] = useState<EventInput[]>();

  const colors = new Map<CalendarEntryEntityTypeEnum, string>([
    [CalendarEntryEntityTypeEnum.Vacation, theme.palette.success.main],
    [CalendarEntryEntityTypeEnum.DayOff, theme.palette.warning.main],
    [CalendarEntryEntityTypeEnum.Ill, theme.palette.error.main],
    [CalendarEntryEntityTypeEnum.HalfDay, '#00eaff'],
    [CalendarEntryEntityTypeEnum.UnpaidLeave, '#6c25be'],
    [CalendarEntryEntityTypeEnum.Custom, '#6c757d'], // Default color for custom types
  ]);



  // Helper function to get color for an event (prefer the absence causale's own colour)
  const getEventColor = (event: CalendarEntryEntity): string => {
    if ((event as any).absenceType?.colour) {
      return (event as any).absenceType.colour;
    }
    if (event.type === CalendarEntryEntityTypeEnum.Custom && event.customType?.colour) {
      return event.customType.colour;
    }
    return colors.get(event.type) || '#6c757d';
  };
  const nextMonth = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }
    calendarApi.next();
    setDate(calendarApi.getDate());
    // fetch eventCount of next month
    fetchEventCount();
  };

  const prevMonth = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }
    calendarApi.prev();
    setDate(calendarApi.getDate());
    // fetch eventCount of previous month
    fetchEventCount();
  };

  const openDetailView = (clickArg: EventClickArg) => {
    const event = clickArg.event;
    setViewEventState({
      isOpened: true,
      id: Number(event.id),
      color: event.backgroundColor,
    });
  };

  function eventOverlaps(start: Date, end: Date = start, idToExlude?: string): boolean {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return true;
    }

    // Normalize dates to start of day (midnight) for proper comparison
    // This ensures we're comparing dates without time components
    const normalizedStart = startOfDay(start);
    const normalizedEnd = startOfDay(end);
    
    // FullCalendar's end date is exclusive, so we add 1 day to the end for comparison
    const endExclusive = addDays(normalizedEnd, 1);
    
    return calendarApi.getEvents().some((event) => {
      // exlude id is useful when this function is used before an update do exlude from the check the updated event
      if (!event.start || event.id === idToExlude) {
        return false;
      }
      
      // Normalize event dates to start of day for comparison
      // Note: event.end is already exclusive (one day ahead) from convertToEvent
      const eventStart = startOfDay(event.start);
      // event.end is already exclusive, so we use it directly after normalizing
      const eventEnd = event.end ? startOfDay(event.end) : addDays(eventStart, 1);
      
      // Check for overlap: two date ranges overlap if they share any common day
      // Range 1: [normalizedStart, endExclusive) - the new event we're checking
      // Range 2: [eventStart, eventEnd) - existing event on calendar
      // Two ranges overlap if: start1 < end2 && start2 < end1
      return normalizedStart < eventEnd && eventStart < endExclusive;
    });
  }

  const addEventFromCursor = async (dateArg: DateClickArg) => {
    if (eventOverlaps(dateArg.date)) {
      return;
    }

    if (cursorType?.type) {
      let type = cursorType.type;
      let customTypeId: number | undefined;

      // Handle custom types from cursor
      if (typeof type === 'string' && type.startsWith('custom-')) {
        const customId = parseInt(type.replace('custom-', ''), 10);
        type = CalendarEntryEntityTypeEnum.Custom;
        customTypeId = customId;
      }

      const entry: AddWorkFormData = {
        startedOn: dateArg.date,
        endedOn: dateArg.date,
        type,
        customTypeId,
        managerNotes: '',
        publicNotes: ''
      }
      await addEvent(entry);
    }
  }

  const addEventFromDate = (dateArg: DateClickArg) => {
    if (eventOverlaps(dateArg.date)) {
      return;
    }
    setAddEventState({
      isOpened: true,
      data: {
        startedOn: dateArg.date,
        endedOn: dateArg.date,
      },
    });
  };

  const highlightToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }
    calendarApi.today();
    setDate(calendarApi.getDate());
  };

  async function addEvent(calendarEntry: AddWorkFormData) {
    if (eventOverlaps(calendarEntry.startedOn, calendarEntry.endedOn)) {
      toast.error(t('employees.calendar.errEntriesOverlap'));
      return;
    }
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }

    const { startedOn, endedOn, type, customTypeId, allDay, startTime, endTime, ...rest } = calendarEntry;

    // Convert custom type string back to proper enum value for backend
    const backendType = type === 'custom' || (typeof type === 'string' && type.startsWith('custom-')) 
      ? CalendarEntryEntityTypeEnum.Custom 
      : type as CalendarEntryEntityTypeEnum;

    // Format dates to avoid timezone issues
    // Create dates at local midnight, then format as YYYY-MM-DD and append T00:00:00.000Z
    const formatDateForBackend = (date: Date): string => {
      const localDate = new Date(date);
      localDate.setHours(0, 0, 0, 0);
      const dateString = format(localDate, 'yyyy-MM-dd');
      return `${dateString}T00:00:00.000Z`;
    };

    // Handle all-day logic: if allDay is true, don't include times (they stay null)
    // If allDay is false, include the start and end times
    const requestData: any = {
      ...rest,
      type: backendType,
      customTypeId,
      startedOn: formatDateForBackend(startedOn),
      endedOn: formatDateForBackend(endedOn),
    };

    // Only include times if not all-day
    if (!allDay) {
      requestData.startTime = startTime;
      requestData.endTime = endTime;
    }

    const seasonEventsId = seasonId === 0 ? (await seasonApi.findMySeasonInProgress()).data.at(-1)?.id : seasonId;

    if (seasonEventsId) {
      try {
        const { data: newEntry } = userMode ? await calendarEntriesApi.createByMyself(
          String(employeeId),
          requestData,
          seasonEventsId
        ) : await calendarEntriesApi.create(
          String(employeeId),
          requestData,
          seasonId
        );
        calendarApi.addEvent(convertToEvent(newEntry));
        // if a new event get added fetch event count
        fetchEventCount();
        if (fetchDaysOffData)
          fetchDaysOffData();
      } catch (error: any) {
        if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
          toast.error(t('employees.calendar.entry.cannotModifyPast'));
        } else {
          toast.error(error?.response?.data?.message || t('employees.calendar.errEntriesOverlap'));
        }
      }
    } else {
      // Modal to ask to add a season
    }
  }

  async function updateEvent(calendarEntry: UpdateEvent, eventId: number) {
    if (eventOverlaps(new Date(calendarEntry.startedOn), new Date(calendarEntry.endedOn), String(eventId))) {
      toast.error(t('employees.calendar.errEntriesOverlap'));
      return;
    }

    const { startedOn, endedOn, publicNotes, managerNotes } = calendarEntry;

    // Format dates to avoid timezone issues
    const formatDateForBackend = (date: Date | string): string => {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const localDate = new Date(dateObj);
      localDate.setHours(0, 0, 0, 0);
      const dateString = format(localDate, 'yyyy-MM-dd');
      return `${dateString}T00:00:00.000Z`;
    };

    const seasonEventsId = seasonId === 0 ? (await seasonApi.findMySeasonInProgress()).data.at(-1)?.id : seasonId;

    if (seasonEventsId) {
      try {
        await calendarEntriesApi.update(
          String(eventId),
          {
            startedOn: formatDateForBackend(startedOn),
            endedOn: formatDateForBackend(endedOn),
            publicNotes,
            managerNotes,
          }
        );
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          calendarApi.getEventById(String(eventId))?.setDates(new Date(calendarEntry.startedOn), new Date(calendarEntry.endedOn));
        }
        fetchEventCount();
        if (fetchDaysOffData)
          fetchDaysOffData();
      } catch (error: any) {
        if (error?.response?.status === 403 && error?.response?.data?.message?.includes('DEPCHIEF users cannot')) {
          toast.error(t('employees.calendar.entry.cannotModifyPast'));
        } else {
          toast.error(error?.response?.data?.message || t('employees.calendar.errEntriesOverlap'));
        }
      }
    }
  }

  function renderEventContent(eventContent: EventContentArg) {
    const event = eventContent.event;
    const isTimedEvent = event.extendedProps?.isTimedEvent;
    const startTime = event.extendedProps?.startTime;
    const endTime = event.extendedProps?.endTime;
    
    // For timed events, show only the time in bold, readable font
    if (isTimedEvent && startTime && endTime) {
      return (
        <div style={{ 
          width: '100%', 
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '13px',
          fontWeight: 'bold',
          color: '#000000',
          textShadow: '0 0 2px rgba(255, 255, 255, 0.8)',
          padding: '4px'
        }}>
          {startTime} - {endTime}
        </div>
      );
    }
    
    // For all-day events, show nothing (just the color)
    return null;
  }

  function convertToEvent(item: CalendarEntryEntity) {
    const eventColor = getEventColor(item);
    
    // Check if this is a timed event (has start and end times)
    const isTimedEvent = item.startTime && item.endTime;
    
    // Create the event title (without time - time will be displayed separately in the cell).
    // Prefer the absence causale's localized label (e.g. "Infortunio") over the legacy type.
    let title = '';
    const at = (item as any).absenceType;
    if (at) {
      const lang = (i18n.language || 'en').slice(0, 2);
      title = lang === 'it' ? at.labelIt : lang === 'de' ? at.labelDe : lang === 'fr' ? at.labelFr : at.labelEn;
    } else if (item.type === CalendarEntryEntityTypeEnum.Custom && item.customType) {
      title = item.customType.name;
    } else {
      title = t(`employees.calendar.types.${item.type.toLowerCase()}`);
    }
    
    // For timed events, we need to combine the date with the time
    let startDateTime = new Date(item.startedOn);
    let endDateTime = new Date(item.endedOn);
    
    if (isTimedEvent && item.startTime && item.endTime) {
      // Parse the time strings and combine with the date
      const [startHours, startMinutes] = item.startTime.split(':').map(Number);
      const [endHours, endMinutes] = item.endTime.split(':').map(Number);
      
      startDateTime = new Date(item.startedOn);
      startDateTime.setHours(startHours, startMinutes, 0, 0);
      
      endDateTime = new Date(item.endedOn);
      endDateTime.setHours(endHours, endMinutes, 0, 0);
    }
    
    return {
      id: String(item.id),
      start: startDateTime,
      // For month view, we'll treat all events as all-day but show time in title
      end: addDays(new Date(item.endedOn), 1),
      allDay: true, // Always true for month view compatibility
      title,
      color: item.accepted ? eventColor : fadeColor(eventColor),
      // Add custom properties for timed events
      extendedProps: {
        startTime: item.startTime,
        endTime: item.endTime,
        isTimedEvent
      }
    };
  }

  const fetchEventCount = async () => {
    const calendarApi = calendarRef.current?.getApi();
    let currentDate;
    if (calendarApi) {
      currentDate = calendarApi.getDate();
    }
    else {
      currentDate = date;
    }
    const eventCount = userMode ? await calendarEntriesApi.findAllEventsByMyself(
      String(employeeId),
      currentDate.getMonth() + 1,
      currentDate.getFullYear(),
    ) : await calendarEntriesApi.findAllEvents(
      String(employeeId),
      String(seasonId),
      currentDate.getMonth() + 1,
      currentDate.getFullYear(),
    );
    setEventCount(eventCount.data as Record<string, number>);
  }

  const fetchData = async () => {
    const calendarEntryEntities = userMode ? (await calendarEntriesApi.findAllByMyself(
      String(employeeId)
    )).data : (await calendarEntriesApi.findAll(
      String(employeeId),
      seasonId
    )    ).data
    
    console.log('Raw calendar entries:', calendarEntryEntities);
    
    // Clear the events from the calendar -> Otherwise new events will be merged with existings one.
    calendarRef.current?.getApi().removeAllEvents()
    const convertedEvents = calendarEntryEntities.map((item) => {
      return convertToEvent(item);
    });
    
    console.log('Converted events:', convertedEvents);
    
    // Debug: Check if events have the right properties
    convertedEvents.forEach((event, index) => {
      if (event.extendedProps?.isTimedEvent) {
        console.log(`Timed event ${index}:`, {
          title: event.title,
          startTime: event.extendedProps.startTime,
          endTime: event.extendedProps.endTime,
          isTimedEvent: event.extendedProps.isTimedEvent
        });
      }
    });
    
    setInitialEvents(convertedEvents);
    // every time data are fetched fetch eventCount
    fetchEventCount();
    if (fetchDaysOffData)
      fetchDaysOffData();
  };

  useEffect(() => {
    fetchData();

  }, [employeeId, seasonId]);

  useEffect(() => {
    toast.dismiss();
    if (cursorType?.type !== null) {
      toast(
        <span>
          {t('employees.calendar.tool')}: {cursorType?.label}
        </span>,
        {
          duration: Infinity,
          icon: <BrushIcon fontSize='small' style={{ color: cursorType?.color }} />,
        });
    }
  }, [cursorType]);

  useEffect(() => {
    // Unmount function to remove the toast
    return () => {
      toast.remove();
    };
  }, []);

  const findSelectedLanguage = () => {
    switch (i18n.language) {
      case 'en':
        return enLocale;
      case 'it':
        return itLocale;
      case 'de':
        return deLocale;
      case 'fr':
        return frLocale;
      default:
        return enLocale;
    }
  }

  const dayCellDidMount = (arg: any) => {
    const date = arg.date;
    const holiday = isHoliday(date);
    
    if (holiday) {
      const dayNumber = arg.el.querySelector('.fc-daygrid-day-number');
      if (dayNumber) {
        dayNumber.classList.add(
          holiday.region === 'ITALY' ? 'holiday-number' : 'regional-holiday-number'
        );
        dayNumber.title = holiday.name;
      }
    }
  };

  return (
    <Box pt={2} pb={4}>
      <Grid container spacing={3}>
        <Toaster />
        <Grid item xs={12}>
          {initialEvents && (
            <Card>
              <HeaderToolbar
                date={date}
                onDatePrev={() => {
                  prevMonth();
                }}
                onDateNext={() => {
                  nextMonth();
                }}
                onAddEventForm={() => {
                  setAddEventState({ isOpened: true });
                }}
                onToday={() => {
                  highlightToday();
                }}
              />
              <CalendarWrapper>
                <FullCalendar
                  timeZone='UTC'
                  eventDisplay='background'
                  initialDate={date}
                  eventClick={openDetailView}
                  dateClick={cursorType?.type === null ? addEventFromDate : addEventFromCursor}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  ref={calendarRef}
                  height={height ?? 600}
                  rerenderDelay={10}
                  events={initialEvents}
                  headerToolbar={false}
                  firstDay={1}
                  eventContent={renderEventContent}
                  locale={findSelectedLanguage()}
                  dayHeaderFormat={{ weekday: isMobile ? 'narrow' : isTablet ? 'short' : 'long', }}
                  dayHeaderContent={(content) => {
                    return firstLetterUpperCase(content.text);
                  }}
                  dayCellDidMount={dayCellDidMount}
                />
              </CalendarWrapper>
            </Card>
          )}
          {addEventState.isOpened && (
            <AddEventModal
              open={addEventState.isOpened}
              onClose={() => {
                setAddEventState({ isOpened: false });
              }}
              onSubmit={async (entry: AddWorkFormData) => {
                await addEvent(entry);
                setAddEventState({ isOpened: false });
              }}
              data={addEventState.data}
            />
          )}

          {viewEventState.isOpened && (
            <ViewEventModal
              open={viewEventState.isOpened}
              onClose={() => {
                setViewEventState({ isOpened: false });
              }}
              id={viewEventState.id ?? 0}
              color={viewEventState.color ?? ''}
              employeeId={employeeId}
              seasonId={seasonId}
              fetchCalendarData={fetchData}
              userMode={userMode}
              updateEvent={updateEvent}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Calendar;
